import GlobalSearchBar from "./components/search-input/GlobalSearchBar";
import GlobalSearchInitialView from "./components/global-search/GlobalSearchInitialView";
import GlobalSearchResultsView from "./components/global-search/GlobalSearchResultsView";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useKeyboardShortcut, useMetaKey } from "@/utils/shortcut";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { SearchIcon } from "lucide-react";
import { useGlobalSearchContext } from "@/providers/global-search/GlobalSearchContext";
import { SearchTag } from "@/components/data-table/search-tag";
import useSearch from "@/hooks/useSearch";
import { useOpenStoredSearch } from "@/hooks/useOpenStoredSearch";
import { buildSearchUrlParams } from "@/utils/searchParamsFormatter";
import { hasSearchContent } from "@/lib/search/storedSearch";
import { GlobalSearchTab } from "@/models/search/Search";
import { withClearedSearchText } from "./utils/searchUtils";
import { SEARCH_MODAL_EXIT_DURATION_MS } from "./hooks/useClearSearchAfterClose";

interface GlobalSearchAppBarProps {
  inputRef: React.RefObject<HTMLInputElement>;
}

export const GlobalSearchAppBar = ({ inputRef }: GlobalSearchAppBarProps) => {
  const { isMac } = useMetaKey();
  const {
    inputValue,
    setInputValue,
    setIsSearchbarOpen,
    isSearchbarOpen,
    searchQuery,
    setSearchQuery,
    globalSearchViewMode,
    expandedCategory,
    setExpandedCategory,
    focusedSource,
    setFocusedSource,
    // Results data
    legislationResults,
    documentResults,
    isLoadingLegislation,
    isLoadingDocument,
    legislationHasMore,
    documentHasMore,
    // Filter configuration and state
    leveledFilters,
    defaultsPlan,
    sources,
    setSources,
    updateFilter,
    sourceCounts,
    legislationTotalRecords,
    documentTotalRecords,
    // Modal infinite scroll support
    loadMoreModalResults,
    isLoadingModalMore,
    accumulatedLegislationResults,
    accumulatedDocumentResults,
  } = useGlobalSearchContext();

  const { createSearch } = useSearch();
  const openStoredSearch = useOpenStoredSearch();

  // Track if a result was clicked this session (to avoid double-saving)
  const resultClickedRef = useRef(false);
  const prevIsSearchbarOpenRef = useRef(isSearchbarOpen);

  const searchShortcutLabel = `${isMac ? "⌘" : "Ctrl"} + K`;

  // Callback to mark that a result was clicked (passed down to result items)
  const markResultClicked = useCallback(() => {
    resultClickedRef.current = true;
  }, []);

  // Save search on modal close if there's an active search and no result was clicked
  useEffect(() => {
    const wasOpen = prevIsSearchbarOpenRef.current;
    const isNowClosed = !isSearchbarOpen;

    if (wasOpen && isNowClosed) {
      // Modal just closed
      const hasActiveSearch = hasSearchContent(
        searchQuery?.searchTerm,
        searchQuery?.searchTags
      );

      if (hasActiveSearch && !resultClickedRef.current) {
        // Save the search
        const params = buildSearchUrlParams(
          searchQuery,
          searchQuery?.searchTerm
        );
        createSearch.mutate({
          searchUrl: `/search?${params.toString()}`,
        });
      }

      // Reset the flag for next session
      resultClickedRef.current = false;
    }

    prevIsSearchbarOpenRef.current = isSearchbarOpen;
  }, [isSearchbarOpen, searchQuery, createSearch]);

  // Reset tab to "All" when opening the GlobalSearch modal
  // This ensures search queries are enabled (they only run for "all" tab)
  useEffect(() => {
    if (isSearchbarOpen && searchQuery?.tab !== GlobalSearchTab.All) {
      setSearchQuery((prev) => ({
        ...prev,
        tab: GlobalSearchTab.All,
      }));
    }
  }, [isSearchbarOpen, searchQuery?.tab, setSearchQuery]);

  useKeyboardShortcut({
    key: "k",
    callback: () => {
      setIsSearchbarOpen(true);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    },
  });

  // Handle escape key - progressive behavior:
  // 1. Focused source -> Clear focus
  // 2. Expanded view -> Results overview
  // 3. Results view (has search) -> Initial view (clear search)
  // 4. Initial view -> Close modal
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();

        if (focusedSource) {
          // Step 1: Clear focused source
          setFocusedSource(null);
        } else if (expandedCategory !== null) {
          // Step 2: Go back from expanded view to results overview
          setExpandedCategory(null);
        } else if (
          searchQuery?.searchTerm ||
          (searchQuery?.searchTags && searchQuery.searchTags.length > 0)
        ) {
          // Step 3: Clear search to go back to initial view
          setSearchQuery(withClearedSearchText(searchQuery));
          setInputValue("");
          inputRef.current?.focus();
        } else if (isSearchbarOpen) {
          // Step 4: Close the modal
          setIsSearchbarOpen(false);
          inputRef.current?.blur();
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSearchbarOpen,
    expandedCategory,
    searchQuery,
    focusedSource,
    setFocusedSource,
  ]);

  const searchOverlay = createPortal(
    <>
      {/* Backdrop — portaled to body so Safari does not clip under overflow ancestors */}
      <AnimatePresence>
        {isSearchbarOpen && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: SEARCH_MODAL_EXIT_DURATION_MS / 1000 }}
            className="fixed inset-0 isolate z-50 bg-background/40 supports-backdrop-filter:backdrop-blur-xs"
            onClick={() => setIsSearchbarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Main Search Modal — inset-x-0 + mx-auto centers without translate so Framer scale anim doesn't break Safari layout */}
      <AnimatePresence>
        {isSearchbarOpen && (
          <motion.div
            key="search-modal"
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{
              duration: SEARCH_MODAL_EXIT_DURATION_MS / 1000,
              ease: "easeOut",
            }}
            className="fixed top-8 inset-x-0 z-50 mx-auto w-[min(80vw,calc(100vw-2rem))] max-w-6xl"
          >
              <div className="flex flex-col h-[90dvh] gap-2">
                {/* Search Bar - styled as its own card */}
                <div className="shrink-0">
                  <GlobalSearchBar inputRef={inputRef} />
                </div>

                {/* Content - its own card */}
                <div className="flex-1 min-h-0 bg-background border rounded-lg shadow-lg overflow-hidden">
                  {globalSearchViewMode === "initial" ? (
                    <GlobalSearchInitialView onSearchSelect={openStoredSearch} />
                  ) : (
                    <GlobalSearchResultsView
                      expandedCategory={expandedCategory}
                      onExpandCategory={setExpandedCategory}
                      legislationResults={legislationResults}
                      workspaceDocResults={documentResults}
                      isLoadingLegislation={isLoadingLegislation}
                      isLoadingWorkspaceDocs={isLoadingDocument}
                      legislationHasMore={legislationHasMore}
                      workspaceDocsHasMore={documentHasMore}
                      onResultClick={markResultClicked}
                      leveledFilters={leveledFilters}
                      defaultsPlan={defaultsPlan}
                      sources={sources}
                      setSources={setSources}
                      filters={searchQuery?.filters || {}}
                      workspaceDocumentFilters={
                        searchQuery?.workspaceDocumentFilters || {}
                      }
                      onFilterChange={updateFilter}
                      sourceCounts={sourceCounts}
                      focusedSource={focusedSource}
                      setFocusedSource={setFocusedSource}
                      legislationTotalRecords={legislationTotalRecords}
                      documentTotalRecords={documentTotalRecords}
                      onLoadMore={loadMoreModalResults}
                      isLoadingMore={isLoadingModalMore}
                      expandedLegislationResults={accumulatedLegislationResults}
                      expandedDocumentResults={accumulatedDocumentResults}
                    />
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </>,
      document.body
    );

  return (
    <>
      {searchOverlay}

      {/* Regular Search Input - Always visible in header */}
      {/* For small screens */}
      <div className="block 2xl:hidden">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSearchbarOpen(true)}
            >
              <SearchIcon className="size-4.5" size={18} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" sideOffset={4}>
            {searchShortcutLabel}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* For large screens */}
      <div className="relative z-10 w-[300px] hidden 2xl:block">
        <div className="relative">
          <SearchIcon
            className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground z-10"
            size={16}
          />
          <div
            className="flex items-center gap-1 pl-8 pr-12 min-h-9 bg-background border rounded-sm shadow-xs cursor-text overflow-hidden"
            onClick={() => setIsSearchbarOpen(true)}
          >
            {searchQuery?.searchTags?.map((tag, index) => (
              <SearchTag key={index} tag={tag} variant="inline" />
            ))}
            {inputValue && (
              <span className="text-sm truncate">{inputValue}</span>
            )}
            {!inputValue &&
              (!searchQuery?.searchTags ||
                searchQuery.searchTags.length === 0) && (
                <span className="text-sm text-muted-foreground">Søg</span>
              )}
          </div>
          <span className="text-muted-foreground text-xs absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
            {searchShortcutLabel}
          </span>
        </div>
      </div>
    </>
  );
};
